import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Collapse, IconButton, Link, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { useSite } from "../../context/siteContext";
import { getActiveBanners } from "../../services/bannerService";
import { trackEvent } from "../../utilities/analyticsService";

const DISMISSED_STORAGE_KEY = "dismissedNoticeBanners";
const BANNER_REFRESH_EVENT = "banner-refresh";

const readDismissed = () => {
	try {
		const parsed = JSON.parse(localStorage.getItem(DISMISSED_STORAGE_KEY) || "[]");
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

const writeDismissed = (items) => {
	try {
		localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(items.slice(-200)));
	} catch {
		// no-op
	}
};

const getDismissKey = (banner) => `${banner?.id || ""}:${banner?.updated_at || banner?.created_at || ""}`;

const getBannerAnalyticsPayload = (
	banner = {},
	{
		user = {},
		legalEntity = "",
		selectedSite = "",
		selectedSiteData = null,
		selectedSiteName = "",
		selectedCustomer = "",
		selectedCustomerName = "",
		selectedCustomerNumber = "",
		selectedSiteCurrency = "",
	} = {}
) => {
	const roleName =
		user?.role?.role_name ||
		user?.role?.role_type ||
		user?.user_role_name ||
		user?.user_role ||
		(typeof user?.role === "string" ? user.role : "") ||
		"";

	return {
		banner_id: banner.id || "",
		banner_title: banner.title || "",
		banner_severity: banner.severity || "",
		cta_label: banner.cta_label || "",
		cta_url: banner.cta_url || "",

		user_id: user?.user_id || user?.id || "",
		user_name:
			user?.user_display_name ||
			user?.user_name ||
			`${user?.user_first_name || ""} ${user?.user_last_name || ""}`.trim() ||
			"",
		user_email: user?.user_email || user?.email || "",
		role_id: user?.role_id || user?.role?.role_id || "",
		user_role: roleName,

		customer_id:
			selectedSiteData?.cus_id ||
			selectedCustomer ||
			"",
		customer_name:
			selectedSiteData?.parent_customer_name ||
			selectedSiteData?.customer_name ||
			selectedCustomerName ||
			"",
		customer_number:
			selectedSiteData?.customer_number ||
			selectedCustomerNumber ||
			"",

		site_id:
			selectedSiteData?.site_id ||
			banner.site_id ||
			"",
		site_name:
			selectedSiteData?.customer_site_name ||
			selectedSiteData?.site_name ||
			selectedSiteName ||
			selectedSite ||
			"",
		site_number:
			selectedSiteData?.customer_site_number ||
			selectedSite ||
			"",
		customer_site_number:
			selectedSiteData?.customer_site_number ||
			selectedSite ||
			"",

		legal_entity:
			selectedSiteData?.site_legal_entity ||
			selectedSiteData?.legal_entity ||
			selectedSiteData?.resolved_legal_entity ||
			legalEntity ||
			"",
		site_legal_entity:
			selectedSiteData?.site_legal_entity ||
			selectedSiteData?.legal_entity ||
			selectedSiteData?.resolved_legal_entity ||
			legalEntity ||
			"",

		currency: selectedSiteCurrency || "",
		location:
			selectedSiteData?.customer_site_name ||
			selectedSiteData?.site_name ||
			selectedSiteName ||
			selectedSite ||
			"",

		source: "banner_notice",
	};
};

const normalizeBanners = (response) => {
	if (Array.isArray(response?.data?.banners)) return response.data.banners;
	if (Array.isArray(response?.banners)) return response.banners;
	return [];
};

const normalizeSeverity = (value) => {
	const normalized = String(value || "info").toLowerCase();
	return ["info", "success", "warning", "error"].includes(normalized) ? normalized : "info";
};

const BannerNotice = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();
	const {
		selectedSite,
		selectedSiteData,
		selectedSiteName,
		selectedSiteLegalEnitity,
		selectedCustomer,
		selectedCustomerName,
		selectedCustomerNumber,
		selectedSiteCurrency,
	} = useSite();

	const [banners, setBanners] = useState([]);
	const [dismissed, setDismissed] = useState(() => readDismissed());
	const [showAllBanners, setShowAllBanners] = useState(false);
	const [expandedBanners, setExpandedBanners] = useState(new Set());
	const viewedBannerKeysRef = useRef(new Set());

	const legalEntity = useMemo(() => {
		if (!selectedSiteData || Array.isArray(selectedSiteData)) return "";

		const rawSiteEntity =
			selectedSiteData?.resolved_legal_entity ??
			selectedSiteData?.site_legal_entity ??
			selectedSiteData?.legal_entity ??
			selectedSiteLegalEnitity ??
			"";

		return String(rawSiteEntity || "").trim().toUpperCase();
	}, [
		selectedSiteData,
		selectedSiteData?.legal_entity,
		selectedSiteData?.resolved_legal_entity,
		selectedSiteData?.site_legal_entity,
		selectedSiteLegalEnitity,
	]);

	const analyticsContext = useMemo(
		() => ({
			user,
			legalEntity,
			selectedSite,
			selectedSiteData,
			selectedSiteName,
			selectedCustomer,
			selectedCustomerName,
			selectedCustomerNumber,
			selectedSiteCurrency,
		}),
		[
			user,
			legalEntity,
			selectedSite,
			selectedSiteData,
			selectedSiteName,
			selectedCustomer,
			selectedCustomerName,
			selectedCustomerNumber,
			selectedSiteCurrency,
		]
	);

	const loadBanners = useCallback(async () => {
		if (!user || !legalEntity) {
			setBanners([]);
			return;
		}

		try {
			const response = await getActiveBanners(legalEntity);

			if (response?.status === false) {
				setBanners([]);
				return;
			}

			setBanners(normalizeBanners(response));
		} catch {
			setBanners([]);
		}
	}, [legalEntity, user]);

	useEffect(() => {
		loadBanners();
	}, [loadBanners]);

	useEffect(() => {
		const handleBannerRefresh = () => {
			loadBanners();
		};

		window.addEventListener(BANNER_REFRESH_EVENT, handleBannerRefresh);

		return () => window.removeEventListener(BANNER_REFRESH_EVENT, handleBannerRefresh);
	}, [loadBanners]);

	const visibleBanners = useMemo(
		() =>
			banners.filter((banner) => {
				if (banner?.dismissible === false) return true;
				return !dismissed.includes(getDismissKey(banner));
			}),
		[banners, dismissed]
	);

	const dismissBanner = (banner) => {
		trackEvent("banner_dismissed", getBannerAnalyticsPayload(banner, analyticsContext));

		const next = [...new Set([...dismissed, getDismissKey(banner)])];

		setDismissed(next);
		writeDismissed(next);
	};

	const trackBannerCtaClick = (banner) => {
		trackEvent("banner_cta_clicked", getBannerAnalyticsPayload(banner, analyticsContext));
	};

	const handleCtaClick = (e, banner) => {
		const ctaUrl = banner.cta_url;

		if (!ctaUrl) return;

		const isInternalLink = String(ctaUrl).startsWith("/");

		if (isInternalLink) {
			e.preventDefault();

			const currentUrl = `${location.pathname}${location.search || ""}`;

			if (currentUrl === ctaUrl || location.pathname === ctaUrl) {
				return;
			}

			navigate(ctaUrl);
		}

		trackBannerCtaClick(banner);
	};

	const toggleBannerExpansion = (bannerKey) => {
		setExpandedBanners((prev) => {
			const next = new Set(prev);

			if (next.has(bannerKey)) {
				next.delete(bannerKey);
			} else {
				next.add(bannerKey);
			}

			return next;
		});
	};

	const isBannerExpanded = (bannerKey) => expandedBanners.has(bannerKey);

	const shouldCollapseText = (text, maxLength) => String(text || "").length > maxLength;

	const getCollapsedText = (text, maxLength) => {
		const normalized = String(text || "");

		if (normalized.length <= maxLength) return normalized;

		return `${normalized.slice(0, maxLength).trimEnd()}...`;
	};

	const renderInlineExpandableText = (text, maxLength, bannerKey, typographyProps = {}) => {
		const collapsed = getCollapsedText(text, maxLength);
		const expandable = shouldCollapseText(text, maxLength);

		return (
			<Typography {...typographyProps}>
				{isBannerExpanded(bannerKey) ? String(text || "") : collapsed}

				{expandable && (
					<Button
						size="small"
						color="inherit"
						onClick={() => toggleBannerExpansion(bannerKey)}
						sx={{
							minWidth: 0,
							ml: 0.5,
							p: 0,
							verticalAlign: "baseline",
							textTransform: "none",
							fontSize: "inherit",
							fontWeight: 700,
							lineHeight: "inherit",
							textDecoration: "underline",
							color: "inherit",
							"&:hover": {
								backgroundColor: "transparent",
								textDecoration: "underline",
							},
						}}
					>
						{isBannerExpanded(bannerKey) ? "Show less" : "View more"}
					</Button>
				)}
			</Typography>
		);
	};

	const hasSiteContext =
		selectedSite ||
		selectedSiteData?.site_id ||
		selectedSiteData?.customer_site_number;

	useEffect(() => {
		if (!hasSiteContext) return;

		visibleBanners.forEach((banner) => {
			const key = `${getDismissKey(banner)}:${selectedSite || selectedSiteData?.site_id || ""}`;

			if (!key || viewedBannerKeysRef.current.has(key)) return;

			viewedBannerKeysRef.current.add(key);
			trackEvent("banner_viewed", getBannerAnalyticsPayload(banner, analyticsContext));
		});
	}, [analyticsContext, visibleBanners, hasSiteContext, selectedSite, selectedSiteData?.site_id]);

	if (!visibleBanners.length) return null;

	const displayBanners = showAllBanners ? visibleBanners : visibleBanners.slice(0, 3);
	const hiddenCount = visibleBanners.length - displayBanners.length;

	return (
		<Box sx={{ px: { xs: 1.5, md: 3 }, pt: 1, pb: 0.5 }}>
			<Stack spacing={1}>
				{displayBanners.map((banner) => (
					<Collapse key={getDismissKey(banner)} in>
						<Alert
							severity={normalizeSeverity(banner.severity)}
							variant="filled"
							sx={{
								alignItems: "center",
								borderRadius: 1,
								"& .MuiAlert-message": {
									width: "100%",
									minWidth: 0,
								},
							}}
							action={
								<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
									{banner.cta_label && banner.cta_url && (
										<Link
											href={banner.cta_url}
											target={String(banner.cta_url).startsWith("/") ? undefined : "_blank"}
											rel={String(banner.cta_url).startsWith("/") ? undefined : "noopener noreferrer"}
											underline="always"
											onClick={(e) => handleCtaClick(e, banner)}
											sx={{
												whiteSpace: "nowrap",
												display: "inline-flex",
												alignItems: "center",
												minHeight: 30,
												px: 1,
												borderRadius: 0.75,
												bgcolor: "#fff",
												color: "#0b57d0",
												fontSize: "0.8125rem",
												fontWeight: 800,
												textDecorationColor: "#0b57d0",
												"&:hover": {
													bgcolor: "#f5f9ff",
													color: "#073b92",
													textDecorationColor: "#073b92",
												},
											}}
										>
											{banner.cta_label}
										</Link>
									)}

									{banner.dismissible !== false && (
										<IconButton
											aria-label="Dismiss banner"
											color="inherit"
											size="small"
											onClick={() => dismissBanner(banner)}
										>
											<CloseIcon fontSize="small" />
										</IconButton>
									)}
								</Box>
							}
						>
							<Box sx={{ minWidth: 0 }}>
								{renderInlineExpandableText(
									banner.title,
									100,
									getDismissKey(banner),
									{
										variant: "body2",
										fontWeight: 700,
										sx: { overflowWrap: "anywhere" },
									}
								)}

								{renderInlineExpandableText(
									banner.message,
									200,
									`${getDismissKey(banner)}-msg`,
									{
										variant: "body2",
										sx: {
											mt: 0.5,
											whiteSpace: "pre-line",
											overflowWrap: "anywhere",
										},
									}
								)}
							</Box>
						</Alert>
					</Collapse>
				))}

				{hiddenCount > 0 && !showAllBanners && (
					<Box sx={{ display: "flex", justifyContent: "center" }}>
						<Button
							size="small"
							onClick={() => setShowAllBanners(true)}
							sx={{
								textTransform: "none",
								fontSize: "0.8125rem",
								fontWeight: 600,
							}}
						>
							+{hiddenCount} more
						</Button>
					</Box>
				)}

				{showAllBanners && visibleBanners.length > 3 && (
					<Box sx={{ display: "flex", justifyContent: "center" }}>
						<Button
							size="small"
							onClick={() => setShowAllBanners(false)}
							sx={{
								textTransform: "none",
								fontSize: "0.8125rem",
								fontWeight: 600,
							}}
						>
							Show less
						</Button>
					</Box>
				)}
			</Stack>
		</Box>
	);
};

export default BannerNotice;